import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';

import {convertOreToKronerString, convertToKroner, phoneNumberWithoutSpaces, toLocalCurrency} from 'utils/number';
import {
	Button,
	CloseIcon,
	Dialog,
	Divider,
	IconButton,
	Layout,
	ListItem,
	Select,
	Table,
	Text,
	// WarningFilledIcon,
} from '@phonectas/unifon-ui-kit';
import InvoiceInfoList from './InvoiceInfoList';
import InvoiceSendSmsContent from './InvoiceSendSmsContent';
import {StickyTableHeader} from './styled';
import VismaActionBtns from 'common/components/VismaActionBtns';
import VismaPostpone from './VismaPostpone';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import {isEmailValid} from 'utils/email';
import VismaTooltipContent from 'common/components/Visma/VismaTooltipContent';
import {isNumberPhone} from 'utils/validators';
import ReportErrorBtn from 'common/components/ReportErrorBtn';
import {CreditingFormProps, getCreditingFormUrl} from 'common/components/CreditingForm';
import {DATE_FORMATS, dateByCustomFormat} from 'utils/date';
import {Invoice, VismaInvoiceLine} from 'common/types/store/threeSixtyTypes';
import CdRatorOwnerSelector from 'common/components/CdRatorOwnerSelector';
import LinearLoader from 'common/components/customLoader/components/LinearLoader';
import Pagination from 'common/components/Pagination';
import CustomTooltip from 'common/components/CustomTooltip';
// import SearchWidget from 'common/components/SearchWidget';

interface Props {
	data: Invoice;
	onClose: () => void;
	id: string;
}

const InvoiceDialogContent: React.FC<Props> = ({data, onClose, id}) => {
	const [t] = useTranslation();
	const {
		downloadInvoice,
		downloadInvoiceLatest,
		changeVismaDueDate,
		getVismaInvoice,
		sendPdf,
		sendSms,
	} = useThreeSixtyNewStore((state) => ({
		downloadInvoice: state.downloadInvoice,
		downloadInvoiceLatest: state.downloadInvoiceLatest,
		changeVismaDueDate: state.changeVismaDueDate,
		getVismaInvoice: state.getVismaInvoice,
		sendPdf: state.sendPdf,
		sendSms: state.sendSms,
	}), shallow);
	const {activeRowVisma, isVismaLoading, customer} = useThreeSixtyNewStore((state) => ({
		activeRowVisma: state?.activeRowVisma,
		visma: state?.accountTables?.visma,
		billingGrp: state?.accountTables?.visma?.all_billing_groups,
		isVismaLoading: state?.isVismaLoading,
		customer: state?.activeAccount,
	}), shallow);
	const dueDate = activeRowVisma?.invoice?.DueDate;
	const amount = activeRowVisma?.invoice?.AmountNet;
	const accDetails = customer?.account_details;
	const dueDateData = useMemo(() => ({
		account_id: id,
		client_number: data?.client_no.toString(),
		due_date: dayjs().toDate(),
		invoice_number: data?.invoice_number,
	}), [data, id]);
	const tableData = activeRowVisma?.lines || [];
	const isInvoiceClosed = data?.calculated_status === 'CLOSED';
	const [changeDueData, setChangeDueData] = useState(dueDateData);
	const [sendSmsDialog, setSendSmsDialog] = useState(false);
	const [invoiceCopy, setInvoiceCopy] = useState(false);
	const [postPoneInvoice, setPostponeInvoice] = useState(false);
	const [email, setEmail] = useState('');
	const [emailLanguage, setEmailLanguage] = useState('NO');
	const [smsContent, setSmsContent] = useState({
		number: '',
		text: '',
	});
	// const [searchQuery, setSearchQuery] = useState('');
	// const handleSearchQuery = useCallback((value: string) => {
	// 	setSearchQuery(value);
	// }, []);
	// const handleClearQuery = useCallback(() => {
	// 	setSearchQuery('');
	// }, []);
	const totalPages = activeRowVisma?.totalPages;

	const invoiceStatus = useMemo(() => {
		if (data?.calculated_status) {
			return data.calculated_status.charAt(0).toUpperCase() + data.calculated_status.slice(1).toLowerCase();
		}
		return '';
	}, [data?.calculated_status]);
	const customerPhone = data?.customer_phone_no_customer;
	const onDateChange = useCallback((date: Date | null) => {
		if (date === null) return;
		setChangeDueData((prevState) => ({
			...prevState,
			due_date: dayjs(date).toDate(),
		}));
	}, []);
	const onHidePostponeInvoice = useCallback(() => {
		onDateChange(dayjs().toDate());
		setPostponeInvoice(false);
	}, [onDateChange]);
	const creditForm: CreditingFormProps = useMemo(() => {
		const creditdueDate = dueDate ? dueDate : '';
		const creditAmount = amount ? amount : undefined;
		return {
			zendesk: '',
			customerName: data?.customer_name ?? '',
			customerNumber: customer?.cdr_account_number ?? '',
			invoiceNumber: data?.invoice_number ?? '',
			dueDate: dateByCustomFormat(creditdueDate, DATE_FORMATS.YYYY_MM_DD) ?? '',
			gsm: accDetails?.products?.mobiles_active_count ?? '0',
			amount: convertOreToKronerString(creditAmount) ?? '',
		}
	}, [data, accDetails, customer?.cdr_account_number, amount, dueDate]);
	const onSendNewDueDate = useCallback(() => {
		const formattedData = {
			...changeDueData,
			due_date: dayjs(changeDueData.due_date, 'DDMMYY').format('DDMMYY'),
		};
		changeVismaDueDate(formattedData, () => {
			onHidePostponeInvoice();
			getVismaInvoice(data?.client_no, data?.invoice_number, 1, 10);
		});
	}, [
		changeDueData,
		changeVismaDueDate,
		data?.client_no,
		data?.invoice_number,
		getVismaInvoice,
		onHidePostponeInvoice
	]);
	const onShowSendSms = useCallback(() => {
		setSendSmsDialog(true);
	}, []);
	const onHideSendSms = useCallback(() => {
		setSendSmsDialog(false);
	}, []);
	const onShowInvoiceCopy = useCallback(() => {
		setInvoiceCopy(true);
	}, []);
	const onHideInvoiceCopy = useCallback(() => {
		setInvoiceCopy(false);
		setEmailLanguage('NO');
		setEmail('');
	}, []);
	const onShowPostponeInvoice = useCallback(() => {
		setPostponeInvoice(true);
	}, []);

	const onNextTwoWeeks = useCallback(() => {
		setChangeDueData((prevState) => ({
			...prevState,
			due_date: dayjs().add(13, 'day').toDate(),
		}));
	}, []);

	const quickRanges = useMemo(() => {
		return (
			<Layout>
				<Button variant="alt" compact onClick={onNextTwoWeeks}>
					{t('14 days')}
				</Button>
			</Layout>
		);
	}, [t, onNextTwoWeeks]);

	const handleSendSms = useCallback(() => {
		sendSms({
			number: smsContent.number,
			text: smsContent.text,
			onSuccess: () => {
				setSmsContent({
					number: '',
					text: '',
				});
				onHideSendSms();
			}
		});
	}, [smsContent, sendSms, onHideSendSms]);

	const handleSendInvoiceCopy = useCallback(() => {
		sendPdf({
			id: id,
			clientNr: data?.client_no.toString(),
			emailReceiver: email,
			invoiceNr: data?.invoice_number,
			language: emailLanguage,
			onSuccess: () => {
				setEmail('');
				setEmailLanguage('NO');
				onHideInvoiceCopy();
			}
		});
	}, [id, data?.client_no, data?.invoice_number, email, sendPdf, emailLanguage, onHideInvoiceCopy]);

	const handleDownloadInvoice = useCallback((button: string) => {
		if (button === 'download-invoice') {
			downloadInvoice(data.client_no, data.invoice_number);
		} else if (button === 'download-latest') {
			downloadInvoiceLatest(data.client_no, data.invoice_number);
		}
	}, [data?.invoice_number, data?.client_no, downloadInvoice, downloadInvoiceLatest]);

	const isDueDateValid = useMemo(() => {
		const originalDate = dayjs();
		const selectedDate = dayjs(changeDueData?.due_date, 'DDMMYY');
		const minDate = originalDate;
		const maxDate = originalDate.add(13, 'day');
	
		return (
			(selectedDate.isSame(minDate) || selectedDate.isAfter(minDate)) &&
        	selectedDate.isSameOrBefore(maxDate)
		);
	}, [changeDueData?.due_date]);

	const isSmsValid = useMemo(() => {
		return isNumberPhone(smsContent?.number) && smsContent?.text;
	}, [smsContent]);

	const handlePageinationChange = useCallback((_: React.ChangeEvent<unknown>, value: number) => {
		getVismaInvoice(data?.client_no, data?.invoice_number, value, 10);
	}, [data?.client_no, data?.invoice_number, getVismaInvoice]);

	const handleEmailChange = useCallback((value: string) => {
		setEmail(value);
	}, []);

	const handleLanguageChange = useCallback((val: string) => {
		setEmailLanguage(val);
	}, []);
	const canSendPdf = useMemo(() => {
		return (
			isEmailValid(email) &&
			emailLanguage &&
			data?.client_no &&
			data?.invoice_number &&
			id
		);
	}, [email, emailLanguage, data?.client_no, data?.invoice_number, id]);

	const handleCreditBtnClick = useCallback(() => {
		const url = getCreditingFormUrl(creditForm);
		window.open(url, '_blank');
	}, [creditForm]);

	const renderItems = useCallback((item: VismaInvoiceLine, index: number) => {
		const showTooltip = item?.AmountNet > 0 && item?.AmountGross > 0 && item?.AmountVat > 0;
		return (
			<Table.Row
				key={index}
				index={index}
				style={{padding: '0'}}
			>
				<Table.Cell>{item?.ItemNumber}</Table.Cell>
				<Table.Cell>{item?.ItemDescr}</Table.Cell>
				<Table.Cell>{item?.Quantity}</Table.Cell>
				<Table.Cell>
					{
						showTooltip ? (
							<Layout style={{cursor: 'pointer'}} fullWidth>
								<CustomTooltip title={<VismaTooltipContent data={item} />}>
									<span>
										{convertToKroner(item?.AmountNet)}
									</span>
								</CustomTooltip>
							</Layout>
						) : convertToKroner(item?.AmountNet)
					}
				</Table.Cell>
				<Table.Cell>{convertToKroner(item?.AmountVat)}</Table.Cell>
				<Table.Cell>{convertToKroner(item?.AmountGross)}</Table.Cell>
				<Table.Cell>
					{`${item?.Volume} ${item?.VolumeUnit ? item?.VolumeUnit : ''}`}
				</Table.Cell>
			</Table.Row>
		);
	}, []);

	useEffect(() => {
		if (customerPhone) {
			setSmsContent((prevState) => ({
				...prevState,
				number: phoneNumberWithoutSpaces(customerPhone),
			}));
		}
	}, [customerPhone]);

	return (
		<>
			<Layout nowrap alignItems="start" fullWidth justifyContent="space-between">
				<Layout nowrap fullWidth alignItems="center" justifyContent="space-between">
					<Layout direction="column" fullWidth nowrap gap="none">
						<Layout
							alignItems="center"
							gap="sm"
							fullWidth
							justifyContent="space-between"
							nowrap
						>
							<Layout gap="xs" alignItems="center" fullWidth>
								<Text variant="h1" style={{color: '#000'}}>
									{toLocalCurrency(activeRowVisma?.invoice?.AmountNet || 0)}
								</Text>
								<Text variant="h1" color="secondary">
									{t('NOK')}
								</Text>
								<Divider
									vertical
									style={{
										height: '24px',
										margin: '0 4px',
									}}
								/>
								<Layout nowrap alignItems="center" gap="xs">
									<Text variant="body1" color="secondary">
										{t('Status')}:
									</Text>
									<Text variant="body1" color="primary">
										{invoiceStatus}
									</Text>
								</Layout>
							</Layout>
							<Layout>
								<ReportErrorBtn
									btnName='credit'
									onClick={handleCreditBtnClick}
								/>
								{/* {
									data?.isWarning && (
										<WarningFilledIcon color="error" />
									)
								} */}
							</Layout>
						</Layout>
					</Layout>
					<IconButton onClick={onClose}><CloseIcon /></IconButton>
				</Layout>
			</Layout>
			<Divider />
			<InvoiceInfoList activeRow={data} />
			<Divider style={{marginBottom: '10px'}} />
			<VismaActionBtns 
				sendSms={onShowSendSms}
				invoiceCopy={onShowInvoiceCopy}
				postPoneInvoice={onShowPostponeInvoice}
				downloadInvoice={handleDownloadInvoice}
				disabled={isInvoiceClosed}
			/>
			<Divider style={{margin: '10px 0px'}} />
			{/* <Layout direction="row">
				<SearchWidget
					placeholder={t('Search')}
					fullWidth
					value={searchQuery}
					onChange={handleSearchQuery}
					clearSearch={handleClearQuery}
				/>
			</Layout> */}
			<Layout
				fullWidth
				direction="column"
				style={{overflow: 'hidden', position: 'relative'}}
				fullHeight
			>
				{
					isVismaLoading ? (
						<LinearLoader isLoading={true} />
					) : (
						<StickyTableHeader
							items={tableData || []}
							renderItem={(item, index) => (renderItems(item, index))}
						>
							<Table.Row style={{padding: '0px'}}>
								<Table.Cell width={90}>{t('Phone number')}</Table.Cell>
								<Table.Cell>{t('Description')}</Table.Cell>
								<Table.Cell width={52}>{t('Amount')}</Table.Cell>
								<Table.Cell width={75}>{t('Ex VAT')}</Table.Cell>
								<Table.Cell width={75}>{t('VAT')}</Table.Cell>
								<Table.Cell width={75}>{t('Inc VAT')}</Table.Cell>
								<Table.Cell width={60}>{t('Volume')}</Table.Cell>
							</Table.Row>
						</StickyTableHeader>
					)
				}
			</Layout>
			{
				totalPages && totalPages > 1 && (
					<Layout fullWidth>
						<Divider style={{margin: '5px 0px'}} />
						<Pagination
							variant="outlined"
							shape="square"
							count={activeRowVisma?.totalPages}
							onChange={handlePageinationChange}
							page={activeRowVisma?.page}
						/>
					</Layout>
				)
			}
			<Dialog
				show={sendSmsDialog}
				onClose={onHideSendSms}
				onOk={handleSendSms}
				id="send-sms-dialog"
				title={t('Send details by SMS')}
				okButtonLabel={t('Send')}
				disableOk={!isSmsValid}
				closeButtonLabel={t('Close')}
				style={{width: '528px'}}
			>
				<InvoiceSendSmsContent 
					data={data}
					smsContent={smsContent}
					setSmsContent={setSmsContent}
				/>
			</Dialog>
			<Dialog
				show={postPoneInvoice}
				onClose={onHidePostponeInvoice}
				onOk={onSendNewDueDate}
				id="send-sms-dialog"
				title={t('Postpone invoice')} 
				okButtonLabel={t('Postpone')}
				disableOk={!isDueDateValid}
				closeButtonLabel={t('Close')}
				style={{width: 'auto'}}
			>
				<VismaPostpone
					quickRanges={quickRanges}
					onDateChange={onDateChange}
					selectedDate={changeDueData.due_date}
				/>
			</Dialog>
			<Dialog
				show={invoiceCopy}
				onClose={onHideInvoiceCopy}
				onOk={handleSendInvoiceCopy}
				id="send-sms-dialog"
				title={t('Send invoice copy')} 
				okButtonLabel={t('Send')}
				disableOk={!canSendPdf}
				closeButtonLabel={t('Close')}
				style={{width: '528px'}}
			>
				<>
					<Layout fullWidth nowrap alignItems="center">
						<Layout style={{width: '100px'}}>
							<Text variant="body1" color="secondary">
								{t('Recipient')}
							</Text>
						</Layout>
						<Layout fullWidth>
							<CdRatorOwnerSelector
								onChange={handleEmailChange}
								variant='mail'
								value={email}
							/>
						</Layout>
					</Layout>
					<Layout fullWidth nowrap alignItems="center">
						<Layout style={{width: '100px'}}>
							<Text variant="body1" color="secondary">
								{t('Language')}
							</Text>
						</Layout>
						<Layout fullWidth>
							<Select
								value={emailLanguage}
								onChange={(val) => handleLanguageChange(val as string)}
							>
								<ListItem value="NO" primaryText={t('Norwegian')} />
								<ListItem value="EN" primaryText={t('English')} />
							</Select>
						</Layout>
					</Layout>
				</>
			</Dialog>
		</>
	);
}

export default InvoiceDialogContent;
